import { useEffect, useMemo, useState } from 'react';

import FormModal from '../../../Modals/FormModal';
import InputField from '../../../Forms/Input/InputField';

import useProductCatalog from '../../../../hooks/productpanel/useProductCatalog';
import useDebounced from '../../../../hooks/useDebounced';

import CategoryButtonSelectable from '../../ProductBrowser/Category/CategoryButtonSelectable';
import ActionButton from '../../../Buttons/ActionButton';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  branchId: number;
  selectedCategoryId: number | null;
  onSelect: (categoryId: number | null) => void;
}

const POSCategoryFilterModal = ({
  isOpen,
  onClose,
  branchId,
  selectedCategoryId,
  onSelect,
}: Props) => {
  const { categories, fetchCategories } = useProductCatalog(branchId);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounced(search, 300);

  const [localCategoryId, setLocalCategoryId] = useState<number | null>(
    selectedCategoryId
  );

  // =====================================================
  // 🔹 Load Categories
  // =====================================================

  useEffect(() => {
    if (isOpen && branchId) {
      fetchCategories();
    }
  }, [isOpen, branchId]);

  // =====================================================
  // 🔹 Sync Selection
  // =====================================================

  useEffect(() => {
    setLocalCategoryId(selectedCategoryId);
  }, [selectedCategoryId]);

  // =====================================================
  // 🔹 Filtered Categories
  // =====================================================

  const filteredCategories = useMemo(() => {
    const searchTerm = debouncedSearch.trim().toLowerCase();

    if (!searchTerm) {
      return categories;
    }

    return categories.filter((category) =>
      category.name.toLowerCase().includes(searchTerm)
    );
  }, [categories, debouncedSearch]);

  // =====================================================
  // 🔹 Confirm
  // =====================================================

  const handleConfirm = () => {
    onSelect(localCategoryId);
    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Seleccionar categoría"
      maxWidth="max-w-3xl"
    >
      {/* ===================================================== */}
      {/* 🔹 Search */}
      {/* ===================================================== */}

      <InputField
        label="Buscar categoría"
        name="search"
        type="text"
        value={search}
        placeholder="Buscar categoría..."
        iconKey="search"
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* ===================================================== */}
      {/* 🔹 Categories */}
      {/* ===================================================== */}

      <div
        className="
          mt-5
          max-h-[350px]
          overflow-y-auto
          rounded-xl
          border
          border-stroke
          bg-slate-50
          p-4
          dark:border-strokedark
          dark:bg-boxdark
        "
      >
        <div
          className="
            grid
            grid-cols-1
            gap-3
            sm:grid-cols-2
          "
        >
          <CategoryButtonSelectable
            id={0}
            name="Todas las categorías"
            isSelected={localCategoryId === null}
            onClick={() => setLocalCategoryId(null)}
            iconName="category"
          />

          {filteredCategories.map((category) => (
            <CategoryButtonSelectable
              key={category.id}
              id={category.id}
              name={category.name}
              isSelected={localCategoryId === category.id}
              onClick={() => setLocalCategoryId(category.id)}
              iconName="category"
            />
          ))}
        </div>

        {filteredCategories.length === 0 && (
          <div
            className="
              py-10
              text-center
              text-sm
              text-body
            "
          >
            No se encontraron categorías.
          </div>
        )}
      </div>

      {/* ===================================================== */}
      {/* 🔹 Actions */}
      {/* ===================================================== */}

      <div className="mt-6 flex justify-end gap-2">

        <ActionButton
          label="Cancelar"
          variant="secondary"
          fullWidth={false}
          onClick={onClose}
        />

        <ActionButton
          label="Seleccionar"
          variant="primary"
          fullWidth={false}
          onClick={handleConfirm}
        />
      </div>
    </FormModal>
  );
};

export default POSCategoryFilterModal;
